import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useTenantContext } from '../../context/TenantContext';
import { toast } from 'sonner';
import { 
  Image as ImageIcon, 
  X, 
  Search,
  Grid3X3,
  List,
  Eye,
  Loader2,
  FolderOpen,
  Check,
  Upload,
  Trash2,
  Copy
} from 'lucide-react';

interface MediaFile {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  created_at: string;
}

interface MediaSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string | string[]) => void;
  selectedUrl?: string;
  selectedUrls?: string[];
  title?: string;
  allowUpload?: boolean;
  multiSelect?: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/svg+xml"];

export function MediaSelector({ 
  isOpen, 
  onClose, 
  onSelect, 
  selectedUrl,
  selectedUrls: initialSelectedUrls = [],
  title = "Sélectionner une image",
  allowUpload = true,
  multiSelect = false
}: MediaSelectorProps) {
  const { tenant } = useTenantContext();
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [dragOver, setDragOver] = useState(false);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialiser la sélection uniquement à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setSelectedUrls(initialSelectedUrls);
    }
  }, [isOpen]);

  // Charger les fichiers uniquement à l'ouverture
  useEffect(() => {
    if (isOpen && tenant) {
      fetchMediaFiles();
    }
  }, [isOpen, tenant]);

  const fetchMediaFiles = async () => {
    if (!tenant) return;
    
    try {
      setLoading(true);
      
      // Lister les fichiers dans le dossier tenant/{tenant_id}
      const { data: fileList, error } = await supabase.storage
        .from('media')
        .list(`tenant/${tenant.id}`, {
          limit: 1000,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        console.error('Erreur lors de la récupération des fichiers:', error);
        toast.error('Erreur lors du chargement des fichiers');
        return;
      }

      // Convertir en format MediaFile avec URLs publiques
      const mediaFiles: MediaFile[] = fileList
        .filter(file => file.name !== '.emptyFolderPlaceholder')
        .map(file => {
          const { data } = supabase.storage
            .from('media')
            .getPublicUrl(`tenant/${tenant.id}/${file.name}`);
          
          return {
            id: file.id || file.name,
            name: file.name,
            url: data.publicUrl,
            size: file.metadata?.size || 0,
            type: file.metadata?.mimetype || 'image/jpeg',
            created_at: file.created_at || new Date().toISOString()
          };
        });

      setFiles(mediaFiles);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des fichiers');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (uploadFiles: FileList | File[]) => {
    if (!tenant) return;

    const fileArray = Array.from(uploadFiles);
    const validFiles = fileArray.filter(file => {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error(`Type de fichier non supporté: ${file.name}`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`Fichier trop volumineux: ${file.name} (max 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    
    try {
      const uploadPromises = validFiles.map(async (file) => {
        const fileExtension = file.name.split('.').pop();
        const uniqueName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExtension}`;
        const filePath = `tenant/${tenant.id}/${uniqueName}`;

        const { error: uploadError } = await supabase.storage
          .from('media')
          .upload(filePath, file);

        if (uploadError) {
          throw uploadError;
        }

        return uniqueName;
      });

      await Promise.all(uploadPromises);
      
      toast.success(`${validFiles.length} fichier(s) uploadé(s) avec succès`);
      await fetchMediaFiles();
      
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error('Erreur lors de l\'upload des fichiers');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const copyToClipboard = async (url: string, fileName: string) => {
    try {
      // Méthode moderne avec l'API Clipboard
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        toast.success(`URL copiée: ${fileName}`);
      } else {
        // Fallback pour les contextes non-sécurisés ou navigateurs anciens
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          toast.success(`URL copiée: ${fileName}`);
        } else {
          throw new Error('Échec de la copie');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
      toast.error('Erreur lors de la copie. Veuillez copier manuellement l\'URL.');
    }
  };

  const deleteFile = async (fileName: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (!tenant) return;
    
    if (!confirm(`Êtes-vous sûr de vouloir supprimer "${fileName}" ?`)) {
      return;
    }

    try {
      const { error } = await supabase.storage
        .from('media')
        .remove([`tenant/${tenant.id}/${fileName}`]);

      if (error) {
        throw error;
      }

      toast.success('Fichier supprimé');
      await fetchMediaFiles();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSelectImage = (url: string) => {
    if (multiSelect) {
      // Mode sélection multiple
      setSelectedUrls(prev => {
        if (prev.includes(url)) {
          return prev.filter(u => u !== url);
        } else {
          return [...prev, url];
        }
      });
    } else {
      // Mode sélection simple - sélection immédiate
      onSelect(url);
    }
  };

  const handleConfirmSelection = () => {
    if (multiSelect && selectedUrls.length > 0) {
      onSelect(selectedUrls);
    }
  };

  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-lg w-full max-w-4xl h-[80vh] flex flex-col border border-white/10">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-md transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Barre d'outils */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center space-x-4 flex-1">
            {allowUpload && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_IMAGE_TYPES.join(',')}
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 text-white px-3 py-2 rounded-md flex items-center gap-2 transition-colors text-sm"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {uploading ? 'Upload...' : 'Uploader'}
                </button>
              </>
            )}
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-amber-600 text-white' 
                    : 'bg-white/5 text-gray-400 hover:text-white'
                }`}
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-amber-600 text-white' 
                    : 'bg-white/5 text-gray-400 hover:text-white'
                }`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {multiSelect && selectedUrls.length > 0 && (
              <span className="text-sm text-amber-400 font-medium">
                {selectedUrls.length} sélectionné(s)
              </span>
            )}
            <span className="text-sm text-gray-400">
              {filteredFiles.length} fichier(s)
            </span>
          </div>
        </div>

        {/* Zone de glisser-déposer */}
        {allowUpload && (
          <div
            className={`m-4 border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
              dragOver 
                ? 'border-amber-500 bg-amber-500/10' 
                : 'border-white/20 bg-white/5 hover:border-white/30'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-white text-sm font-medium mb-1">
              Glissez-déposez vos images ici ou cliquez pour sélectionner
            </p>
            <p className="text-gray-400 text-xs">
              Formats supportés: JPG, PNG, WebP, SVG (max 10MB)
            </p>
          </div>
        )}

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">
                {searchTerm ? 'Aucun fichier trouvé' : 'Aucun fichier dans votre médiathèque'}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {searchTerm ? 'Essayez un autre terme de recherche' : 'Allez dans la médiathèque pour uploader vos premières images'}
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredFiles.map((file) => (
                <div
                  key={file.id}
                  className={`relative group bg-white/5 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                    (multiSelect ? selectedUrls.includes(file.url) : selectedUrl === file.url)
                      ? 'border-amber-500 bg-amber-500/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  onClick={() => handleSelectImage(file.url)}
                >
                  <div className="aspect-square relative">
                    <img
                      src={file.url}
                      alt={file.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    
                    {/* Overlay avec actions */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(file.url, file.name);
                          }}
                          className="p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                          title="Copier l'URL"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(file.url, '_blank');
                          }}
                          className="p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                          title="Voir en grand"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteFile(file.name, e);
                          }}
                          className="p-2 bg-red-500/80 hover:bg-red-600 rounded-full text-white transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Indicateur de sélection */}
                    {(multiSelect ? selectedUrls.includes(file.url) : selectedUrl === file.url) && (
                      <div className="absolute top-2 right-2 bg-amber-500 rounded-full p-1">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                    
                    {/* Compteur pour multi-sélection */}
                    {multiSelect && selectedUrls.includes(file.url) && (
                      <div className="absolute top-2 left-2 bg-amber-500 rounded-full w-6 h-6 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          {selectedUrls.indexOf(file.url) + 1}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Informations du fichier */}
                  <div className="p-3">
                    <p className="text-white text-sm font-medium truncate" title={file.name}>
                      {file.name}
                    </p>
                    <p className="text-gray-400 text-xs">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white/5 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Aperçu</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Nom</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Taille</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredFiles.map((file) => (
                    <tr 
                      key={file.id} 
                      className={`hover:bg-white/5 cursor-pointer ${
                        (multiSelect ? selectedUrls.includes(file.url) : selectedUrl === file.url) ? 'bg-amber-500/10' : ''
                      }`}
                      onClick={() => handleSelectImage(file.url)}
                    >
                      <td className="px-4 py-3">
                        <div className="relative">
                          <img
                            src={file.url}
                            alt={file.name}
                            className="w-12 h-12 object-cover rounded"
                            loading="lazy"
                          />
                          {(multiSelect ? selectedUrls.includes(file.url) : selectedUrl === file.url) && (
                            <div className="absolute -top-1 -right-1 bg-amber-500 rounded-full p-0.5">
                              <Check className="h-3 w-3 text-white" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-white font-medium">{file.name}</td>
                      <td className="px-4 py-3 text-gray-400">{formatFileSize(file.size)}</td>
                      <td className="px-4 py-3 text-gray-400">
                        {new Date(file.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(file.url, file.name);
                            }}
                            className="p-1 text-gray-400 hover:text-white transition-colors"
                            title="Copier l'URL"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(file.url, '_blank');
                            }}
                            className="p-1 text-gray-400 hover:text-white transition-colors"
                            title="Voir en grand"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteFile(file.name, e);
                            }}
                            className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pied de page */}
        <div className="flex items-center justify-between p-4 border-t border-white/10">
          <div>
            {multiSelect && selectedUrls.length > 0 && (
              <button
                onClick={() => setSelectedUrls([])}
                className="px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Désélectionner tout
              </button>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Annuler
            </button>
            {multiSelect ? (
              <button
                onClick={() => {
                  handleConfirmSelection();
                  onClose();
                }}
                disabled={selectedUrls.length === 0}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 disabled:text-gray-400 text-white rounded-md transition-colors"
              >
                Sélectionner ({selectedUrls.length})
              </button>
            ) : (
              <button
                onClick={onClose}
                disabled={!selectedUrl}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 disabled:text-gray-400 text-white rounded-md transition-colors"
              >
                Sélectionner
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
